import { promptInApp } from "../common/AppDialog";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Responsive, WidthProvider } from "react-grid-layout";
import {
  ArrowLeft,
  BookOpen,
  Check,
  ChevronRight,
  CornerUpLeft,
  FileText,
  FolderOpen,
  FolderPlus,
  Move,
  Plus,
  RotateCcw,
  X,
} from "lucide-react";
import {
  createText,
  isFolderNote,
  moveText,
  moveTextUnderParent,
  saveCorkboardScopeLayout,
  setTextKind,
  subscribeSettings,
  subscribeWorkspace,
  toSortedArray,
} from "../../services/workspaceService";
import { useEditLock } from "../../context/EditLockContext";
import { getRememberedWritingText, rememberWritingText, rememberedTextPath } from "../../utils/writingSession";
import { navigateToText } from "../../utils/textRoutes";
import { READING } from "../../config/readingStates";
import {
  CORK_COLS,
  CORK_DEFAULT_H,
  CORK_DEFAULT_W,
  CORK_MAX_H,
  CORK_MAX_W,
  CORK_ROW_H,
  folderScopeKey,
  notebookScopeKey,
  packMissingCards,
  snapLayout,
  textsInScope,
} from "../../utils/corkboardScope";

const ResponsiveGridLayout = WidthProvider(Responsive);

function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}

function textDate(text) {
  if (!text?.updatedAt) return "";
  return new Date(text.updatedAt).toLocaleDateString("es-AR", { day: "numeric", month: "short" });
}

function visibleText(text) {
  return text?.readingState !== READING.ARCHIVED && text?.readingState !== READING.TRASH;
}

function flattenNotebooks(workspace) {
  const out = [];
  for (const desk of workspace?.desks || []) {
    for (const drawer of toSortedArray(desk.drawers)) {
      for (const notebook of toSortedArray(drawer.notebooks)) {
        out.push({
          deskId: desk.id,
          deskName: desk.name,
          deskSlug: desk.slug,
          drawerId: drawer.id,
          drawerName: drawer.name,
          drawerSlug: drawer.slug,
          notebookId: notebook.id,
          notebookName: notebook.name,
          notebookSlug: notebook.slug,
          notebookColor: notebook.color,
          texts: toSortedArray(notebook.texts),
        });
      }
    }
  }
  return out;
}

function childrenOf(texts, parentId) {
  return (texts || []).filter((text) => (text.parentId || null) === (parentId || null));
}

function descendantIds(texts, rootId) {
  const out = [];
  const visit = (id) => {
    for (const child of childrenOf(texts, id)) {
      out.push(child.id);
      visit(child.id);
    }
  };
  visit(rootId);
  return out;
}

function topSelection(texts, ids) {
  const selected = new Set(ids);
  return ids.filter((id) => {
    let cur = texts.find((text) => text.id === id);
    const seen = new Set();
    while (cur?.parentId && !seen.has(cur.parentId)) {
      if (selected.has(cur.parentId)) return false;
      seen.add(cur.parentId);
      cur = texts.find((text) => text.id === cur.parentId);
    }
    return true;
  });
}

function locationForText(notebooks, textId) {
  for (const notebook of notebooks) {
    const text = notebook.texts.find((item) => item.id === textId);
    if (text) return { notebook, text };
  }
  return null;
}

function dropTarget(layout, dragged) {
  if (!dragged) return null;
  const cx = dragged.x + dragged.w / 2;
  const cy = dragged.y + dragged.h / 2;
  const candidates = layout.filter((item) => item.i !== dragged.i);
  return (
    candidates.find(
      (item) => cx >= item.x && cx <= item.x + item.w && cy >= item.y && cy <= item.y + item.h,
    ) || null
  );
}

export default function ManuscriptCorkboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const [workspace, setWorkspace] = useState({ desks: [] });
  const [settings, setSettings] = useState({});
  const [notebookId, setNotebookId] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [selected, setSelected] = useState([]);
  const [moving, setMoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [toast, setToast] = useState("");
  const undoRef = useRef(null);
  const dragMovedRef = useRef(false);

  const returnToText = location.state?.returnToText || getRememberedWritingText();

  useEffect(() => {
    const unsubW = subscribeWorkspace(setWorkspace);
    const unsubS = subscribeSettings(setSettings);
    return () => {
      unsubW();
      unsubS();
    };
  }, []);

  const notebooks = useMemo(() => flattenNotebooks(workspace), [workspace]);

  useEffect(() => {
    if (notebookId && notebooks.some((item) => item.notebookId === notebookId)) return;
    const rememberedNotebook = returnToText?.notebookId;
    if (rememberedNotebook && notebooks.some((item) => item.notebookId === rememberedNotebook)) {
      setNotebookId(rememberedNotebook);
      return;
    }
    if (notebooks[0]) setNotebookId(notebooks[0].notebookId);
  }, [notebookId, notebooks, returnToText?.notebookId]);

  const activeNotebook = useMemo(
    () => notebooks.find((item) => item.notebookId === notebookId) || null,
    [notebookId, notebooks],
  );
  const notebookTexts = activeNotebook?.texts || [];
  const currentFolderId = folderStack.length ? folderStack[folderStack.length - 1].id : null;
  const currentFolder = currentFolderId
    ? notebookTexts.find((text) => text.id === currentFolderId) || null
    : null;
  const scopeKey = activeNotebook
    ? currentFolderId
      ? folderScopeKey(currentFolderId)
      : notebookScopeKey(activeNotebook.notebookId)
    : null;

  const boardTexts = useMemo(
    () => textsInScope(notebookTexts, currentFolderId).filter(visibleText),
    [currentFolderId, notebookTexts],
  );

  useEffect(() => {
    const visibleIds = new Set(boardTexts.map((text) => text.id));
    setSelected((ids) => ids.filter((id) => visibleIds.has(id)));
  }, [boardTexts]);

  const layout = useMemo(() => {
    if (!scopeKey) return [];
    const saved =
      settings.corkboardScopes?.[scopeKey] ||
      (!currentFolderId ? settings.freeformLayout : null) ||
      [];
    const savedMap = new Map((saved || []).map((item) => [String(item.i), item]));
    const kept = boardTexts
      .filter((text) => savedMap.has(String(text.id)))
      .map((text) => {
        const item = savedMap.get(String(text.id));
        return {
          i: String(text.id),
          x: item.x,
          y: item.y,
          w: Math.min(item.w || CORK_DEFAULT_W, CORK_MAX_W),
          h: Math.min(item.h || CORK_DEFAULT_H, CORK_MAX_H),
        };
      });
    return snapLayout(
      packMissingCards(
        kept,
        boardTexts.map((text) => String(text.id)),
        CORK_DEFAULT_W + 2,
        CORK_DEFAULT_H + 2,
      ),
    ).map((item) => ({
      ...item,
      w: Math.min(item.w, CORK_DEFAULT_W),
      h: Math.min(item.h, CORK_DEFAULT_H),
    }));
  }, [boardTexts, currentFolderId, scopeKey, settings.corkboardScopes, settings.freeformLayout]);

  const boardMinHeight = useMemo(() => {
    const maxY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
    return Math.max(660, (maxY + 20) * (CORK_ROW_H + 8));
  }, [layout]);

  const notify = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  }, []);

  const persistLayout = useCallback(
    (nextLayout) => {
      if (locked || !scopeKey) return;
      saveCorkboardScopeLayout(scopeKey, snapLayout(nextLayout));
    },
    [locked, scopeKey],
  );

  const openText = useCallback(
    (text) => {
      if (!activeNotebook || !text) return;
      const enriched = { ...text, ...activeNotebook };
      rememberWritingText(enriched);
      navigateToText(navigate, enriched);
    },
    [activeNotebook, navigate],
  );

  const enterBoard = useCallback(
    async (text) => {
      if (!activeNotebook || !text) return;
      if (!isFolderNote(text)) {
        if (locked) return;
        await setTextKind(
          activeNotebook.deskId,
          activeNotebook.drawerId,
          activeNotebook.notebookId,
          text.id,
          "folder",
        );
      }
      setFolderStack((stack) => [...stack, { id: text.id, title: text.title || "Sin título" }]);
      setSelected([]);
      setMoving(false);
    },
    [activeNotebook, locked],
  );

  const createHere = useCallback(
    async (kind) => {
      if (locked || !activeNotebook) return;
      const title = await promptInApp({
        title: kind === "folder" ? "Nueva carpeta" : "Nueva nota",
        confirmLabel: "Crear",
      });
      if (!title?.trim()) return;
      const id = await createText(
        activeNotebook.deskId,
        activeNotebook.drawerId,
        activeNotebook.notebookId,
        title.trim(),
        "",
        null,
        {
          kind,
          ...(currentFolderId ? { parentId: currentFolderId } : {}),
        },
      );
      if (kind === "note") {
        openText({ id, title: title.trim(), kind: "note", parentId: currentFolderId || undefined });
      }
    },
    [activeNotebook, currentFolderId, locked, openText],
  );

  const toggleSelected = useCallback((id) => {
    setSelected((ids) => (ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]));
  }, []);

  const moveRoots = useCallback(
    async ({ rootIds, destinationNotebook, parentId = null, targetText = null }) => {
      if (!activeNotebook || !rootIds.length || busy) return;
      setBusy(true);
      try {
        const roots = topSelection(notebookTexts, rootIds);
        const movedSubtreeIds = [];
        for (const rootId of roots) {
          movedSubtreeIds.push(rootId, ...descendantIds(notebookTexts, rootId));
        }
        const movingSet = new Set(movedSubtreeIds);
        if (parentId && movingSet.has(parentId)) {
          notify("No podés mover una ficha dentro de sí misma.");
          return;
        }

        const records = movedSubtreeIds
          .map((id) => notebookTexts.find((text) => text.id === id))
          .filter(Boolean)
          .map((text) => ({ id: text.id, parentId: text.parentId || null }));

        let promotedTarget = null;
        if (targetText && targetText.kind !== "folder") {
          await setTextKind(
            destinationNotebook.deskId,
            destinationNotebook.drawerId,
            destinationNotebook.notebookId,
            targetText.id,
            "folder",
          );
          promotedTarget = targetText.id;
        }

        const sameNotebook = destinationNotebook.notebookId === activeNotebook.notebookId;
        if (sameNotebook) {
          for (const rootId of roots) {
            await moveTextUnderParent(
              activeNotebook.deskId,
              activeNotebook.drawerId,
              activeNotebook.notebookId,
              rootId,
              parentId,
            );
          }
        } else {
          // Preserve complete subtrees. moveText clears notebook-local parent links,
          // so descendants are restored after they land in the destination notebook.
          for (const record of records) {
            await moveText(
              activeNotebook.deskId,
              activeNotebook.drawerId,
              activeNotebook.notebookId,
              record.id,
              destinationNotebook.deskId,
              destinationNotebook.drawerId,
              destinationNotebook.notebookId,
            );
            const restoredParent = roots.includes(record.id) ? parentId : record.parentId;
            if (restoredParent) {
              await moveTextUnderParent(
                destinationNotebook.deskId,
                destinationNotebook.drawerId,
                destinationNotebook.notebookId,
                record.id,
                restoredParent,
              );
            }
          }
        }

        undoRef.current = {
          sourceNotebook: activeNotebook,
          destinationNotebook,
          records,
          roots,
          promotedTarget,
        };
        setSelected([]);
        setMoving(false);
        notify(`${roots.length} ficha${roots.length === 1 ? "" : "s"} movida${roots.length === 1 ? "" : "s"}. Ctrl/Cmd+Z deshace.`);
      } catch (error) {
        console.error("[ManuscriptCorkboard] move failed:", error);
        notify("No se pudo mover. La estructura quedó protegida.");
      } finally {
        setBusy(false);
      }
    },
    [activeNotebook, busy, notebookTexts, notify],
  );

  const undoMove = useCallback(async () => {
    const undo = undoRef.current;
    if (!undo || busy) return;
    setBusy(true);
    try {
      const { sourceNotebook, destinationNotebook, records, roots, promotedTarget } = undo;
      const sameNotebook = sourceNotebook.notebookId === destinationNotebook.notebookId;
      if (sameNotebook) {
        for (const record of records.filter((item) => roots.includes(item.id))) {
          await moveTextUnderParent(
            sourceNotebook.deskId,
            sourceNotebook.drawerId,
            sourceNotebook.notebookId,
            record.id,
            record.parentId,
          );
        }
      } else {
        for (const record of records) {
          await moveText(
            destinationNotebook.deskId,
            destinationNotebook.drawerId,
            destinationNotebook.notebookId,
            record.id,
            sourceNotebook.deskId,
            sourceNotebook.drawerId,
            sourceNotebook.notebookId,
          );
          if (record.parentId) {
            await moveTextUnderParent(
              sourceNotebook.deskId,
              sourceNotebook.drawerId,
              sourceNotebook.notebookId,
              record.id,
              record.parentId,
            );
          }
        }
      }
      if (promotedTarget) {
        await setTextKind(
          destinationNotebook.deskId,
          destinationNotebook.drawerId,
          destinationNotebook.notebookId,
          promotedTarget,
          "note",
        );
      }
      undoRef.current = null;
      notify("Movimiento deshecho.");
    } catch (error) {
      console.error("[ManuscriptCorkboard] undo failed:", error);
      notify("No se pudo deshacer automáticamente.");
    } finally {
      setBusy(false);
    }
  }, [busy, notify]);

  const moveUp = useCallback(() => {
    if (!selected.length || !activeNotebook) return;
    const parentOfCurrent = currentFolder?.parentId || null;
    moveRoots({ rootIds: selected, destinationNotebook: activeNotebook, parentId: parentOfCurrent });
  }, [activeNotebook, currentFolder?.parentId, moveRoots, selected]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z" && undoRef.current) {
        event.preventDefault();
        undoMove();
      }
      if (event.key === "Escape") {
        if (moving) setMoving(false);
        else if (selected.length) setSelected([]);
        else if (folderStack.length) setFolderStack((stack) => stack.slice(0, -1));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [folderStack.length, moving, selected.length, undoMove]);

  const wherePath = activeNotebook
    ? [activeNotebook.deskName, activeNotebook.drawerName, activeNotebook.notebookName, ...folderStack.map((f) => f.title)].join(" › ")
    : "—";

  return (
    <div className="mcork">
      <header className="mcork__header">
        <div className="mcork__title-row">
          <div className="mcork__heading">
            <h1>Corcho</h1>
            <p title={wherePath}>{wherePath}</p>
          </div>
          <div className="mcork__actions">
            {!locked && (
              <>
                <button onClick={() => createHere("note")} title="Nueva nota"><Plus size={18} /></button>
                <button onClick={() => createHere("folder")} title="Nueva carpeta"><FolderPlus size={18} /></button>
              </>
            )}
            {returnToText?.id && (
              <button onClick={() => navigate(rememberedTextPath(returnToText))} title="Volver al texto"><BookOpen size={18} /></button>
            )}
            <button onClick={() => navigate("/")} title="Salir"><X size={18} /></button>
          </div>
        </div>

        <div className="mcork__toolbar">
          <button className={folderStack.length ? "" : "is-on"} onClick={() => { setFolderStack([]); setSelected([]); }}>Raíz</button>
          {folderStack.length > 0 && (
            <button onClick={() => { setFolderStack((stack) => stack.slice(0, -1)); setSelected([]); }}>
              <ArrowLeft size={13} /> Subir nivel
            </button>
          )}
          <span>{boardTexts.length} fichas · posición libre</span>
          {selected.length > 0 && (
            <>
              <strong>{selected.length} seleccionada{selected.length === 1 ? "" : "s"}</strong>
              {folderStack.length > 0 && <button onClick={moveUp}><CornerUpLeft size={13} /> Sacar</button>}
              <button className={moving ? "is-on" : ""} onClick={() => setMoving((value) => !value)}><Move size={13} /> Mover…</button>
              <button onClick={() => setSelected([])}>Limpiar</button>
            </>
          )}
          {undoRef.current && <button onClick={undoMove}><RotateCcw size={13} /> Deshacer</button>}
          {notebooks.length > 0 && (
            <select
              value={notebookId || ""}
              onChange={(event) => {
                setNotebookId(event.target.value);
                setFolderStack([]);
                setSelected([]);
                setMoving(false);
              }}
            >
              {notebooks.map((notebook) => (
                <option key={notebook.notebookId} value={notebook.notebookId}>
                  {notebook.deskName} · {notebook.drawerName} · {notebook.notebookName}
                </option>
              ))}
            </select>
          )}
        </div>
      </header>

      {moving && selected.length > 0 && (
        <MovePanel
          notebooks={notebooks}
          sourceNotebook={activeNotebook}
          selected={selected}
          onMove={(destinationNotebook, parentId, targetText) =>
            moveRoots({ rootIds: selected, destinationNotebook, parentId, targetText })
          }
          onClose={() => setMoving(false)}
        />
      )}

      <div className="mcork__board" style={{ minHeight: boardMinHeight }}>
        {!activeNotebook ? (
          <p className="mcork__empty">No hay cuadernos.</p>
        ) : boardTexts.length === 0 ? (
          <p className="mcork__empty">Corcho vacío — creá una nota o carpeta.</p>
        ) : (
          <ResponsiveGridLayout
            className="mcork__grid"
            layouts={{ lg: layout }}
            breakpoints={{ lg: 0 }}
            cols={{ lg: CORK_COLS }}
            rowHeight={CORK_ROW_H}
            margin={[10, 10]}
            draggableHandle=".mcork__drag"
            draggableCancel="button"
            compactType={null}
            preventCollision={false}
            allowOverlap
            isBounded={false}
            isDraggable={!locked}
            isResizable={false}
            onDragStart={(_layout, _old, item) => {
              dragMovedRef.current = false;
              setDraggingId(item?.i || null);
            }}
            onDrag={() => {
              dragMovedRef.current = true;
            }}
            onDragStop={async (nextLayout, _old, item) => {
              setDraggingId(null);
              persistLayout(nextLayout);
              const draggedId = String(item?.i || "");
              const target = dropTarget(nextLayout, item);
              const ids = selected.includes(draggedId) ? selected : [draggedId];
              if (target && !ids.includes(String(target.i))) {
                const targetText = boardTexts.find((text) => String(text.id) === String(target.i));
                if (targetText) {
                  await moveRoots({
                    rootIds: ids,
                    destinationNotebook: activeNotebook,
                    parentId: targetText.id,
                    targetText,
                  });
                }
              }
              window.setTimeout(() => { dragMovedRef.current = false; }, 100);
            }}
            onLayoutChange={() => {}}
            useCSSTransforms
          >
            {boardTexts.map((text) => {
              const folder = isFolderNote(text);
              const chosen = selected.includes(String(text.id)) || selected.includes(text.id);
              const childCount = childrenOf(notebookTexts, text.id).length;
              const summary = stripHtml(text.content);
              return (
                <div key={String(text.id)} className={`mcork__shell${draggingId === String(text.id) ? " is-dragging" : ""}`}>
                  <article className={`mcork__card mcork__drag${folder ? " is-folder" : ""}${chosen ? " is-selected" : ""}`}>
                    <button
                      type="button"
                      className="mcork__select"
                      onClick={(event) => { event.stopPropagation(); toggleSelected(text.id); }}
                      title={chosen ? "Quitar de selección" : "Seleccionar"}
                      aria-pressed={chosen}
                    >
                      {chosen ? <Check size={12} /> : null}
                    </button>
                    <span className="mcork__pin" aria-hidden />
                    <div className="mcork__meta">
                      <span>{folder ? <><FolderOpen size={11} /> Carpeta</> : textDate(text)}</span>
                      {childCount > 0 && <span>{childCount} dentro</span>}
                    </div>
                    <h2 onClick={() => openText(text)}>{text.title || "Sin título"}</h2>
                    {summary ? <p onClick={() => openText(text)}>{summary}</p> : <p className="is-empty">Sin descripción</p>}
                    <div className="mcork__card-actions">
                      <button onClick={() => openText(text)}>Editar</button>
                      <button onClick={() => enterBoard(text)}>{folder ? "Entrar" : "Usar como carpeta"} <ChevronRight size={11} /></button>
                    </div>
                  </article>
                </div>
              );
            })}
          </ResponsiveGridLayout>
        )}
      </div>

      {toast && <div className="mcork__toast">{toast}</div>}
      <style>{CSS}</style>
    </div>
  );
}

function MovePanel({ notebooks, sourceNotebook, selected, onMove, onClose }) {
  const [open, setOpen] = useState({});
  const selectedSet = useMemo(() => new Set(selected.map(String)), [selected]);

  const renderText = (notebook, text, depth = 0) => {
    const kids = childrenOf(notebook.texts, text.id).filter(visibleText);
    const blocked = selectedSet.has(String(text.id));
    const expanded = !!open[text.id];
    return (
      <div key={`${notebook.notebookId}:${text.id}`}>
        <div className="mcork__move-row" style={{ paddingLeft: 10 + depth * 15 }}>
          {kids.length > 0 ? (
            <button className="mcork__twisty" onClick={() => setOpen((value) => ({ ...value, [text.id]: !value[text.id] }))}>
              <ChevronRight size={12} style={{ transform: expanded ? "rotate(90deg)" : "none" }} />
            </button>
          ) : <span className="mcork__twisty" />}
          {isFolderNote(text) ? <FolderOpen size={13} /> : <FileText size={13} />}
          <button
            className="mcork__destination"
            disabled={blocked}
            onClick={() => onMove(notebook, text.id, text)}
            title={blocked ? "Es parte de la selección" : `Mover dentro de ${text.title || "Sin título"}`}
          >
            {text.title || "Sin título"}
          </button>
        </div>
        {expanded && kids.map((child) => renderText(notebook, child, depth + 1))}
      </div>
    );
  };

  return (
    <aside className="mcork__move-panel">
      <div className="mcork__move-head">
        <strong>Mover {selected.length} ficha{selected.length === 1 ? "" : "s"}</strong>
        <button onClick={onClose}><X size={16} /></button>
      </div>
      <p>Elegí un cuaderno (raíz) o cualquier nota/carpeta. Una nota se vuelve carpeta al recibir contenido.</p>
      <div className="mcork__move-tree">
        {notebooks.map((notebook) => (
          <section key={notebook.notebookId}>
            <button className="mcork__notebook-target" onClick={() => onMove(notebook, null, null)}>
              <BookOpen size={13} /> {notebook.deskName} › {notebook.drawerName} › {notebook.notebookName}
              {sourceNotebook?.notebookId === notebook.notebookId ? " · actual" : ""}
            </button>
            {childrenOf(notebook.texts, null).filter(visibleText).map((text) => renderText(notebook, text))}
          </section>
        ))}
      </div>
    </aside>
  );
}

const CSS = `
.mcork{min-height:calc(100vh - 64px);background:#12100e;color:#f2e6d2;padding:14px 14px 92px;box-sizing:border-box;position:relative;overflow-x:hidden}
.mcork button,.mcork select{font:inherit}.mcork__header{margin-bottom:10px}.mcork__title-row{display:flex;justify-content:space-between;gap:12px;align-items:flex-start}.mcork__heading{min-width:0}.mcork__heading h1{margin:0;font-family:"Playfair Display",Georgia,serif;font-size:clamp(1.7rem,5vw,2.25rem)}.mcork__heading p{margin:5px 0 0;color:rgba(242,230,210,.58);font-size:.76rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:72vw}.mcork__actions{display:flex;gap:5px}.mcork__actions button{width:42px;height:42px;border-radius:50%;border:1px solid rgba(242,230,210,.16);background:rgba(0,0,0,.4);color:#f2e6d2;display:grid;place-items:center;cursor:pointer}
.mcork__toolbar{display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-top:9px}.mcork__toolbar button,.mcork__toolbar select{min-height:38px;border:1px solid rgba(242,230,210,.16);border-radius:999px;background:rgba(0,0,0,.35);color:rgba(242,230,210,.78);padding:0 11px;cursor:pointer;display:inline-flex;align-items:center;gap:4px}.mcork__toolbar button.is-on{border-color:#b8952e;color:#d4af37}.mcork__toolbar span{font-size:.74rem;color:rgba(242,230,210,.5)}.mcork__toolbar strong{font-size:.74rem;color:#d4af37}.mcork__toolbar select{margin-left:auto;max-width:min(320px,65vw);border-radius:9px}
.mcork__board{position:relative;background:#c4a06a;background-image:radial-gradient(circle at 18% 28%,rgba(90,55,25,.22) 0 1.2px,transparent 1.6px),radial-gradient(circle at 72% 58%,rgba(70,40,15,.18) 0 1px,transparent 1.4px),repeating-linear-gradient(90deg,rgba(140,95,45,.06) 0 2px,transparent 2px 8px);border:1px solid rgba(70,42,16,.35);box-shadow:inset 0 0 60px rgba(50,28,8,.22);padding:14px;overflow:auto}.mcork__empty{text-align:center;color:rgba(40,25,10,.72);font-weight:650;margin:48px 12px}.mcork__grid .react-grid-item{transition:transform 180ms cubic-bezier(.2,0,0,1)}.mcork__grid .react-grid-item.react-draggable-dragging{transition:none;z-index:50}.mcork__grid .react-grid-placeholder{background:rgba(60,35,10,.16);border:1px dashed rgba(60,35,10,.4)}
.mcork__shell{height:100%}.mcork__card{position:relative;height:100%;box-sizing:border-box;display:flex;flex-direction:column;gap:6px;padding:18px 13px 10px;background:#f4ecd8;color:#2a1f14;border:1px solid rgba(80,55,25,.18);box-shadow:1px 2px 0 rgba(0,0,0,.06),2px 5px 12px rgba(0,0,0,.18);cursor:grab;user-select:none;overflow:hidden}.mcork__card.is-folder{background:#ebe0c4;border-left:3px solid #b8952e}.mcork__card.is-selected{outline:3px solid rgba(120,74,24,.75);outline-offset:1px}.mcork__shell.is-dragging .mcork__card{cursor:grabbing;box-shadow:6px 14px 28px rgba(0,0,0,.35);transform:rotate(-1deg) scale(1.02)}.mcork__select{position:absolute;left:7px;top:7px;width:18px;height:18px;border-radius:4px;border:1px solid rgba(60,35,10,.38);background:rgba(255,255,255,.35);color:#583b1a;padding:0;display:grid;place-items:center;cursor:pointer;z-index:3}.mcork__pin{position:absolute;top:7px;left:50%;width:11px;height:11px;margin-left:-5px;border-radius:50%;background:radial-gradient(circle at 35% 35%,#f2f2f2 0 28%,#8b1c1c 32% 100%);box-shadow:0 1px 2px rgba(0,0,0,.35)}.mcork__meta{margin-top:4px;display:flex;justify-content:space-between;gap:4px;color:#6a5740;font-size:.6rem;text-transform:uppercase;font-weight:700}.mcork__meta span{display:inline-flex;align-items:center;gap:3px}.mcork__card h2{margin:0;font:700 .96rem/1.24 "Playfair Display",Georgia,serif;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden;cursor:text}.mcork__card p{margin:0;color:#6a5740;font-size:.71rem;line-height:1.35;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:3;overflow:hidden;flex:1;cursor:text}.mcork__card p.is-empty{opacity:.55;font-style:italic}.mcork__card-actions{display:flex;justify-content:space-between;gap:7px}.mcork__card-actions button{border:0;background:transparent;color:#76551e;padding:3px 0;font-size:.64rem;font-weight:800;text-transform:uppercase;cursor:pointer;display:inline-flex;align-items:center;gap:2px}
.mcork__move-panel{position:fixed;z-index:1900;top:82px;right:12px;bottom:88px;width:min(420px,calc(100vw - 24px));background:rgba(20,17,14,.97);border:1px solid rgba(212,175,55,.28);box-shadow:0 20px 50px rgba(0,0,0,.45);border-radius:14px;padding:12px;box-sizing:border-box;display:flex;flex-direction:column}.mcork__move-head{display:flex;justify-content:space-between;align-items:center;color:#d4af37}.mcork__move-head button{border:0;background:transparent;color:#f2e6d2;cursor:pointer}.mcork__move-panel>p{font-size:.72rem;color:rgba(242,230,210,.55);margin:6px 0 10px}.mcork__move-tree{overflow:auto;flex:1}.mcork__move-tree section{border-top:1px solid rgba(255,255,255,.06);padding:6px 0}.mcork__notebook-target,.mcork__destination{border:0;background:transparent;color:#e8dcc8;text-align:left;cursor:pointer}.mcork__notebook-target{width:100%;padding:7px 5px;font-weight:700;display:flex;gap:5px;align-items:center}.mcork__move-row{display:flex;align-items:center;gap:4px;min-height:30px;color:#a89f91}.mcork__twisty{width:20px;height:20px;padding:0;border:0;background:transparent;color:#a89f91;display:grid;place-items:center}.mcork__destination{padding:4px 2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.mcork__destination:disabled{opacity:.28;cursor:not-allowed}
.mcork__toast{position:fixed;z-index:2100;left:50%;bottom:90px;transform:translateX(-50%);background:rgba(20,17,14,.96);border:1px solid rgba(212,175,55,.3);color:#f2e6d2;border-radius:10px;padding:9px 13px;font-size:.76rem;box-shadow:0 8px 24px rgba(0,0,0,.35)}
@media(max-width:720px){.mcork{padding:10px 8px 96px}.mcork__toolbar select{order:10;margin-left:0;width:100%;max-width:none}.mcork__board{padding:7px}.mcork__move-panel{top:62px;bottom:82px;right:7px;width:calc(100vw - 14px)}}
`;
